import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';

const NodeFunctionSelector = ({ nodeId, disabled = false, onFunctionCountChange = () => {} }) => {
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState(null);

  const fetchFunctions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/functions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFunctions(data);
      } else {
        setError('Error fetching functions');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error fetching functions');
    }
  };

  const fetchSelectedFunctions = async () => {
    if (!nodeId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedFunctions(data);
        onFunctionCountChange(data.length);
      } else {
        setError('Error fetching selected functions');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error fetching selected functions');
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchFunctions();
      if (nodeId) {
        await fetchSelectedFunctions();
      }
      setLoading(false);
    };
    
    loadData();
  }, [nodeId]);

  // Update available functions when either functions or selected functions change
  useEffect(() => {
    const selectedIds = selectedFunctions.map(f => f.id);
    setAvailableFunctions(functions.filter(f => !selectedIds.includes(f.id)));
  }, [functions, selectedFunctions]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const addFunction = async (functionId) => {
    if (!nodeId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions/${functionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        await fetchSelectedFunctions();
        handleCloseDialog();
      } else {
        setError('Error adding function');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error adding function');
    }
  };

  const removeFunction = async (functionId) => {
    if (!nodeId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions/${functionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        await fetchSelectedFunctions();
      } else {
        setError('Error removing function');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error removing function');
    }
  };

  const renderFunctionTypeChip = (functionType) => {
    const typeMap = {
      'http_request': { label: 'HTTP', color: 'primary' },
      'webhook': { label: 'Webhook', color: 'secondary' },
      'news': { label: 'News', color: 'success' },
    };
    
    const config = typeMap[functionType] || { label: functionType, color: 'default' };
    
    return (
      <Chip 
        size="small" 
        label={config.label} 
        color={config.color} 
        sx={{ marginLeft: 1 }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">
          Functions
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          disabled={disabled || availableFunctions.length === 0}
        >
          Add
        </Button>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={20} />
        </Box>
      ) : selectedFunctions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
            No functions attached
          </Typography>
        </Paper>
      ) : (
        <List dense>
          {selectedFunctions.map((func) => (
            <ListItem key={func.id} divider sx={{ py: 0.5 }}>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{func.name}</Typography>
                    {renderFunctionTypeChip(func.function_type)}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" noWrap>{func.description}</Typography>
                }
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  size="small"
                  onClick={() => removeFunction(func.id)}
                  disabled={disabled}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Dialog for adding a function */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Function to Node</DialogTitle>
        <DialogContent dividers>
          {availableFunctions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1" color="textSecondary">
                No available functions to add
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                All functions are already attached or you need to create new functions
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                sx={{ mt: 1 }}
                onClick={() => window.location.href = '/functions'}
              >
                Create New Function
              </Button>
            </Box>
          ) : (
            <List>
              {availableFunctions.map((func) => (
                <ListItem 
                  key={func.id} 
                  button 
                  onClick={() => addFunction(func.id)}
                  divider
                >
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {func.name}
                        {renderFunctionTypeChip(func.function_type)}
                      </Box>
                    }
                    secondary={func.description}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NodeFunctionSelector; 